// Fixes an unhandled promise rejection thrown by the "predictive-search" custom
// element (Noibu issue #13, related to #12).
//
// Root cause: PredictiveSearch#_onInputChanged (in theme.js) wraps a call to an
// async method (_doPredictiveSearch / _doFallbackSearch) in a synchronous
// try/catch. Because the returned Promise is never awaited or .catch()'d, an
// AbortError thrown when a previous request is cancelled (via
// this._abortController.abort(), which happens on every keystroke) becomes an
// unhandled promise rejection instead of being caught by the existing
// try/catch, which only catches synchronous throws.
//
// Fix, two independent layers:
//
// 1) Patch window.customElements.define so that, the moment "predictive-search"
//    is registered, its prototype's _onInputChanged is replaced with a version
//    that also catches rejections from the returned promise. This script is
//    loaded as a classic (non-module, non-defer) script before
//    vendor.min.js/theme.js, so it always executes before theme.js runs
//    customElements.define("predictive-search", ...) -- meaning the fix is in
//    place before any <predictive-search> element connects to the DOM and
//    binds its input listener to this method.
//
// 2) A window-level "unhandledrejection" safety net, independent of (1). Noibu
//    kept seeing this exact error signature for weeks after (1) shipped, so
//    rather than rely solely on the prototype patch taking effect, this also
//    suppresses the specific, known-benign AbortError at the window level --
//    whatever leaked past (1), this still catches it. It's scoped narrowly
//    (AbortError + this exact message) so it only silences this known pattern,
//    not AbortErrors in general.
(function () {
  if (!window.customElements || typeof window.customElements.define !== "function") {
    return;
  }

  var originalDefine = window.customElements.define.bind(window.customElements);

  window.customElements.define = function (name, constructor, options) {
    if (
      name === "predictive-search" &&
      constructor &&
      constructor.prototype &&
      typeof constructor.prototype._onInputChanged === "function"
    ) {
      constructor.prototype._onInputChanged = function () {
        if (this._queryInput.value === "") {
          return this._onSearchCleared();
        }
        this._abortController?.abort();
        this._abortController = new AbortController();
        try {
          var searchPromise = this.supportsPredictiveApi() ? this._doPredictiveSearch() : this._doFallbackSearch();
          return searchPromise.catch((e) => {
            if (e.name !== "AbortError") {
              throw e;
            }
          });
        } catch (e) {
          if (e.name !== "AbortError") {
            throw e;
          }
        }
      };
    }
    return originalDefine(name, constructor, options);
  };

  window.addEventListener("unhandledrejection", function (event) {
    var reason = event.reason;
    if (
      reason &&
      reason.name === "AbortError" &&
      typeof reason.message === "string" &&
      reason.message.indexOf("signal is aborted") !== -1
    ) {
      event.preventDefault();
    }
  });
})();
